import type { HoursSchedule } from "@starsach/shared-types";

export interface HoursNow {
  open: boolean;
  reason: "open" | "closed" | "holiday";
  holiday?: string;
  announcement?: string;
}

/**
 * The same decision the lander makes at call time, for the screen's
 * "right now" line - not authoritative, and never sent anywhere. Mirrors
 * services/customer/src/lander/handler.ts decideHours.
 */
export function hoursNow(schedule: HoursSchedule, now: Date = new Date()): HoursNow {
  const { day, minutes, date } = localParts(schedule, now);
  const holiday = (schedule.holidays ?? []).find((h) => h.date === date);
  if (holiday) {
    const open = holiday.closed ? false : within(minutes, holiday.opens, holiday.closes);
    return { open, reason: "holiday", holiday: holiday.name, ...(holiday.announcement && !open ? { announcement: holiday.announcement } : {}) };
  }
  const open = schedule.weekly.filter((w) => w.day === day).some((w) => within(minutes, w.opens, w.closes));
  return open ? { open: true, reason: "open" } : { open: false, reason: "closed", ...(schedule.closed_announcement ? { announcement: schedule.closed_announcement } : {}) };
}

function localParts(schedule: HoursSchedule, now: Date): { day: number; minutes: number; date: string } {
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat("en-GB", { timeZone: schedule.timezone, weekday: "short", hour: "2-digit", minute: "2-digit", year: "numeric", month: "2-digit", day: "2-digit", hour12: false });
  } catch {
    fmt = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", hour: "2-digit", minute: "2-digit", year: "numeric", month: "2-digit", day: "2-digit", hour12: false });
  }
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value]));
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return { day: days.indexOf(String(parts["weekday"])), minutes: Number(parts["hour"]) % 24 * 60 + Number(parts["minute"]), date: `${parts["year"]}-${parts["month"]}-${parts["day"]}` };
}

function within(minutes: number, opens: string | undefined, closes: string | undefined): boolean {
  if (!opens || !closes) return false;
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  return minutes >= toMin(opens) && minutes < toMin(closes);
}
